import { defineStore } from 'pinia'
import { useMapSettingsStore } from '@/stores/mapSettingsStore'
import { useMapTerritoryStore } from '@/stores/mapTerritoryStore'
import { useStarStore } from '@/stores/starStore'

export const usePlayerColorStore = defineStore('playerColorStore', {
  state: () => ({
    // Map of playerId -> hex color string (e.g., "#FF0000")
    colors: new Map<number, string>(),
    // Update counter to trigger reactivity when colors change
    updateCounter: 0
  }),
  getters: {
    /**
     * Get color for a specific player
     */
    getColorForPlayer: (state) => (playerId: number): string | null => {
      return state.colors.get(playerId) || null
    },

    /**
     * Check if we have a color for a player
     */
    hasColorForPlayer: (state) => (playerId: number): boolean => {
      return state.colors.has(playerId)
    }
  },
  actions: {
    /**
     * Update player colors from WebSocket data
     * @param data Object with playerId (as string keys) -> hex color (e.g., { "123": "#FF0000" })
     */
    updatePlayerColors(data: Record<string, string>) {
      // Clear existing colors
      this.colors.clear()

      // Add new colors, converting string keys to numbers
      Object.entries(data).forEach(([playerId, color]) => {
        this.colors.set(parseInt(playerId, 10), color)
      })

      // Increment counter to trigger reactivity
      this.updateCounter++

      // Trigger territory update if territories are visible
      this.triggerTerritoryUpdate()
    },

    /**
     * Set color for a specific player
     */
    setPlayerColor(playerId: number, color: string) {
      this.colors.set(playerId, color)
      this.updateCounter++

      // Trigger territory update if territories are visible
      this.triggerTerritoryUpdate()
    },

    /**
     * Remove color for a specific player
     */
    removePlayerColor(playerId: number) {
      this.colors.delete(playerId)
      this.updateCounter++
    },

    /**
     * Clear all player colors
     */
    clearAllColors() {
      this.colors.clear()
      this.updateCounter++
    },

    /**
     * Helper to trigger territory update when colors change
     * Uses lazy getter to avoid circular dependency
     */
    triggerTerritoryUpdate() {
      // Stores are lazy-initialized so circular dependencies are fine
      const mapSettingsStore = useMapSettingsStore()

      if (mapSettingsStore.showTerritoryOverlay) {
        const mapTerritoryStore = useMapTerritoryStore()
        const starStore = useStarStore()

        // Recalculate ownership for all stars before updating territories
        for (const star of starStore.stars.values()) {
          starStore.recalculateStarOwnership(star.id)
        }

        mapTerritoryStore.updateTerritories(starStore.stars)
      }
    }
  }
})
